import React, { useEffect, useState, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { Formik, Field } from 'formik';
import { Button } from 'reactstrap';
import { I18N, I18NContext } from 'commons';
import * as yup from 'yup';
import _ from 'lodash';
import ErrorMessage from '../ErrorMessage';
import TimeRangeSelector from '../../../commons/TimeRangeSelector';
import WeeklySelector from '../../../commons/WeeklySelector';

const QuietTimeForm = ({
  quietTimeFrame,
  timeZone,
  autoFocus,
  onSubmit,
  onClickCancel,
  quietTimeId
}) => {
  const i18n = useContext(I18NContext);

  const { days = [], name, fromHour, fromMin, toHour, toMin } = quietTimeFrame || {};

  const [timeValues, updateTimeValues] = useState({
    fromHour: fromHour || 0,
    fromMin: fromMin || 0,
    toHour: toHour || 0,
    toMin: toMin || 0
  });

  const [quietTimeNameDays, updateQuietTimeDays] = useState(days.concat());
  const renderTimeRangeComponent = () => (
    <TimeRangeSelector
      values={timeValues}
      onChange={(updatedTime) => {
        updateTimeValues(updatedTime);
      }}
      timeZone={timeZone}
      quietTimeId={quietTimeId}
    />
  );


  let timeRangeComponent = renderTimeRangeComponent();

  useEffect(() => {
    timeRangeComponent = renderTimeRangeComponent();
  }, [timeZone]);

  const quietTimeNameRef = useRef(null);
  useEffect(() => {
    if (autoFocus && quietTimeNameRef.current) {
      quietTimeNameRef.current.focus();
    }
  }, [autoFocus]);

  return (
    <Formik
      initialValues={{ quietTimeName: name || '', quietTimeNameDays: days }}
      validationSchema={
        yup.object().shape({
          quietTimeName: yup.string().max(40, 'jquery.validation.maxlength,40').required('jquery.validation.field.required, contact.path.device.time.frame.quiet.time.name'),
          quietTimeNameDays: yup.array().min(1, 'profile.quiet.time.days.required').required('profile.quiet.time.days.required')
        })
      }
      onSubmit={(values) => {
        const newQuietTimeFrame = {
          name: values.quietTimeName,
          days: values.quietTimeNameDays,
          ...timeValues
        };
        if (onSubmit) {
          onSubmit(newQuietTimeFrame);
        }
      }}
    >
      {({
        touched,
        errors,
        isValidating,
        handleSubmit,
        setFieldValue,
        setFieldTouched
      }) => {
        if (isValidating) {
          setTimeout(() => {
            if (!_.isEmpty(errors)) {
              const errorList = Object.keys(errors);
              if (errorList.findIndex(error => error === 'quietTimeName') !== -1) {
                document.getElementById('quietTimeName').focus();
              } else if (errorList.findIndex(error => error === 'quietTimeNameDays') !== -1) {
                document.getElementById('quietTimeNameDays').focus();
              }
            }
          }, 100);
        }
        return (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label id="quietTimeNameLabel" htmlFor="quietTimeName">
                <I18N>contact.path.device.time.frame.quiet.time.name</I18N>
                <span aria-hidden="true" className="required-star">*</span>
                <span className="sr-only"><I18N>information.label.required</I18N></span>
              </label>
              <Field
                type="text"
                name="quietTimeName"
                id="quietTimeName"
                innerRef={quietTimeNameRef}
                maxLength="40"
                autoComplete="off"
                role="textbox"
                className="form-control"
                aria-labelledby="quietTimeNameLabel quietTimeNameError"
                aria-invalid={touched.quietTimeName && !!errors.quietTimeName}
              />
              {
                touched.quietTimeName && errors.quietTimeName &&
                    <ErrorMessage id="quietTimeNameError">{errors.quietTimeName}</ErrorMessage>
              }
            </div>
            <div className="form-group">
              <label id="quietTimeEveryLabel" htmlFor="quietTimeNameDays">
                <I18N>contact.path.device.time.frame.quiet.time.every</I18N>
              </label>
              <div className="row m-0">
                <WeeklySelector
                  role="group"
                  tabIndex={-1}
                  id="quietTimeNameDays"
                  values={quietTimeNameDays}
                  onChange={(updatedDays) => {
                    setFieldValue('quietTimeNameDays', updatedDays);
                    updateQuietTimeDays(updatedDays);
                    setFieldTouched('quietTimeNameDays', true);
                  }}
                  aria-labelledby={`quietTimeEveryLabel${(touched.quietTimeNameDays && errors.quietTimeNameDays) ? ' quietTimeDaysError' : ''}`}
                />
              </div>
              {
                touched.quietTimeNameDays && errors.quietTimeNameDays &&
                    <ErrorMessage id="quietTimeDaysError">{errors.quietTimeNameDays}</ErrorMessage>
              }
            </div>
            <div className="form-group">
              <div>
                {
                  timeRangeComponent &&
                    <TimeRangeSelector
                      values={timeValues}
                      onChange={(updatedTime) => {
                        updateTimeValues(updatedTime);
                      }}
                      timeZone={timeZone}
                      quietTimeId={quietTimeId}
                    />
                }
              </div>
            </div>
            <div className="text-right">
              <Button
                color="secondary"
                className="mr-2 rounded-0 p-1"
                value={i18n['global.dialog.button.cancel']}
                onClick={onClickCancel}
                eb-test="quiet_time_modal_cancel_button"
              >
                <I18N>global.dialog.button.cancel</I18N>
              </Button>
              <Button
                color="primary"
                className="rounded-0 p-1"
                value={i18n['button.save']}
                type="submit"
                eb-test="quiet_time_modal_save_button"
              >
                <I18N>button.save</I18N>
              </Button>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};

QuietTimeForm.propTypes = {
  quietTimeFrame: PropTypes.shape(PropTypes.object),
  onSubmit: PropTypes.func.isRequired,
  timeZone: PropTypes.string.isRequired,
  onClickCancel: PropTypes.func,
  autoFocus: PropTypes.bool,
  quietTimeId: PropTypes.string,
};

QuietTimeForm.defaultProps = {
  quietTimeFrame: null,
  onClickCancel: () => {},
  autoFocus: false,
  quietTimeId: ''
};

export default QuietTimeForm;
